import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { useFetchFamilyData } from '@/components/settings/family/hooks/useFetchFamilyData';
import { useCreateFamily } from '@/components/settings/family/hooks/useCreateFamily';
import CreateFamilyDialog from '@/components/settings/family/CreateFamilyDialog';
import { getStoredChildren } from '@/utils/storage';
import { logger } from '@/utils/logger';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PostSignupFlow = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [showCreateFamilyDialog, setShowCreateFamilyDialog] = useState(false);
  const [hasCheckedChildren, setHasCheckedChildren] = useState(false);
  const [showSkipButton, setShowSkipButton] = useState(false);

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      } else {
        // If no user, redirect to login
        logger.warn('No user found in PostSignupFlow, redirecting to login');
        navigate('/login');
      }
    };

    getCurrentUser();
  }, [navigate]);

  // Fetch family data
  const { isLoading, family, fetchFamilyData } = useFetchFamilyData(userId);

  // Create family hook
  const { isCreating, createFamily } = useCreateFamily(userId, () => {
    setShowCreateFamilyDialog(false);
    fetchFamilyData(); // Refresh family data after creation
  });

  // Trigger initial family data fetch when userId is available
  useEffect(() => {
    if (userId) {
      logger.log('Fetching family data for user:', userId);
      fetchFamilyData();
    }
  }, [userId, fetchFamilyData]);

  // Show skip button after 8 seconds if still loading
  useEffect(() => {
    if (isLoading && userId) {
      const timeout = setTimeout(() => {
        logger.warn('Family data fetch taking too long, showing skip button');
        setShowSkipButton(true);
      }, 8000);

      return () => clearTimeout(timeout);
    }
  }, [isLoading, userId]);

  // Check setup status and navigate accordingly
  useEffect(() => {
    if (!userId || isLoading) return;

    const checkSetupStatus = async () => {
      logger.log('Checking setup status. Family:', family);

      // If no family, show create family dialog
      if (!family) {
        logger.log('No family found, showing create family dialog');
        setShowCreateFamilyDialog(true);
        return;
      }

      // If family exists but haven't checked children yet
      if (!hasCheckedChildren) {
        setHasCheckedChildren(true);

        // Check if user has children
        const children = await getStoredChildren();
        logger.log('Children found:', children?.length || 0);

        if (!children || children.length === 0) {
          // No children, redirect to children page
          logger.log('No children found, redirecting to /children');
          navigate('/children');
        } else {
          // Has children, go to dashboard
          logger.log('Setup complete, redirecting to dashboard');
          navigate('/app');
        }
      }
    };

    checkSetupStatus();
  }, [userId, isLoading, family, hasCheckedChildren, navigate]);

  const handleCreateFamily = async (familyName: string) => {
    await createFamily(familyName);
  };

  const handleSkip = () => {
    logger.log('User clicked skip, showing create family dialog');
    setShowCreateFamilyDialog(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground mb-4">Setting up your account...</p>
        {showSkipButton && (
          <Button onClick={handleSkip} variant="outline">
            Continue Setup
          </Button>
        )}
      </div>

      <CreateFamilyDialog
        open={showCreateFamilyDialog}
        onOpenChange={(open) => {
          // Only allow closing if family has been created
          if (!open && family) {
            setShowCreateFamilyDialog(false);
          } else if (!open && !family) {
            // If trying to close without creating family, skip to dashboard
            logger.log('User skipped family creation, redirecting to dashboard');
            navigate('/app');
          }
        }}
        onCreateFamily={handleCreateFamily}
      />
    </div>
  );
};

export default PostSignupFlow;
